import axios from 'axios';
import Config from 'react-native-config';
import remoteConfig from '@react-native-firebase/remote-config';

const BASE_URL_PROD = 'https://baratao.herokuapp.com/api';
const BASE_URL_HOMOLOG = 'https://baratao3hmg-e06eeb0207c4.herokuapp.com/api';

const USER_AGENT = 'barataoapp';

let apiUrl: string;
let apiUrlv2: string;

switch (Config.RN_ENVIRONMENT) {
  case 'production':
    apiUrl = `${BASE_URL_PROD}/v1`;
    apiUrlv2 = `${BASE_URL_PROD}/v2`;
    break;
  case 'staging':
  case 'development':
    apiUrl = `${BASE_URL_HOMOLOG}/v1`;
    apiUrlv2 = `${BASE_URL_HOMOLOG}/v2`;
    break;
  case 'devtools':
    apiUrl = '';
    apiUrlv2 = '';
    break;
  default:
    apiUrl = `${BASE_URL_PROD}/v1`;
    apiUrlv2 = `${BASE_URL_PROD}/v2`;
    break;
}

const api = axios.create({ baseURL: apiUrl });
const apiv2 = axios.create({ baseURL: apiUrlv2 });

// Configurar User-Agent para todas as instâncias
api.defaults.headers.common['User-Agent'] = USER_AGENT;
apiv2.defaults.headers.common['User-Agent'] = USER_AGENT;

/* ── Rate-limit local (proteção contra Rack Attack) ──────────────── */
const RATE_LIMIT_MAX = 20;       // máximo de requests na janela
const RATE_LIMIT_WINDOW = 5000;  // janela em ms
const RATE_LIMIT_DELAY = 5000;   // atraso quando excede

const requestTimestamps: number[] = [];
let throttleUntil = 0;

function rateLimitInterceptor(config: any): Promise<any> {
  const now = Date.now();

  // Remove timestamps fora da janela
  while (requestTimestamps.length > 0 && requestTimestamps[0] <= now - RATE_LIMIT_WINDOW) {
    requestTimestamps.shift();
  }

  // Se já estamos em período de throttle, atrasa a request
  if (now < throttleUntil) {
    const wait = throttleUntil - now;
    throttleUntil += 200; // escalona requests enfileiradas em 200ms
    return new Promise(resolve => setTimeout(resolve, wait)).then(() => {
      requestTimestamps.push(Date.now());
      return config;
    });
  }

  // Se atingiu o limite, ativa throttle
  if (requestTimestamps.length >= RATE_LIMIT_MAX) {
    throttleUntil = now + RATE_LIMIT_DELAY;
    const wait = RATE_LIMIT_DELAY;
    return new Promise(resolve => setTimeout(resolve, wait)).then(() => {
      requestTimestamps.push(Date.now());
      return config;
    });
  }

  requestTimestamps.push(now);
  return Promise.resolve(config);
}

api.interceptors.request.use(rateLimitInterceptor);
apiv2.interceptors.request.use(rateLimitInterceptor);

api.interceptors.response.use(
  response => response,
  error => {
    const url = error.config?.url || 'URL desconhecida';
    if (error.response) {
      console.error(`Erro na resposta da API do endpoint ${url}:`, error.response.status, error.response.data);
    } else if (error.request) {
      console.error(`Nenhuma resposta recebida ao tentar acessar ${url}:`, error.request);
    } else {
      console.error('Erro na configuração da solicitação:', error.message);
    }
    return Promise.reject(error);
  }
);

const apiZignet = axios.create({ baseURL: Config.RN_API_ZIGNET });
apiZignet.defaults.headers.common['X-API-KEY'] = Config.RN_X_API_KEY_ZIGNET;
apiZignet.defaults.headers.common['User-Agent'] = USER_AGENT;

const apiFipe = axios.create({ baseURL: Config.RN_API_FIPE });
apiFipe.defaults.headers.common['User-Agent'] = USER_AGENT;

const apiLecupon = axios.create({ baseURL: Config.RN_API_LECUPON });
apiLecupon.defaults.headers.common['User-Agent'] = USER_AGENT;

const apiMasterClin = axios.create({
  baseURL: Config.RN_API_MASTERCLIN,
});

apiMasterClin.defaults.headers.common['mc-api-key'] =
  Config.RN_MC_API_KEY_MASTERCLIN;
apiMasterClin.defaults.headers.common['User-Agent'] = USER_AGENT;

export const chavePublicaPKCS = Config.RN_PKCS_ZIGNET as string;
export const tokenFipe = Config.RN_TOKEN_FIPE;

const apiRastrear = axios.create({ baseURL: apiUrlv2 });
apiRastrear.defaults.headers.common['User-Agent'] = USER_AGENT;
apiRastrear.interceptors.request.use(rateLimitInterceptor);

export const barataoApis = [api, apiv2, apiRastrear];

export function setApiUrl(socketUrl: string) {
  api.defaults.baseURL = socketUrl;
  apiv2.defaults.baseURL = socketUrl.replace('v1', 'v2');
  apiRastrear.defaults.baseURL = socketUrl.replace('v1', 'v2');

  barataoApis.length = 0;
  barataoApis.push(api, apiv2, apiRastrear);

  return { api, apiv2, apiRastrear };
}

export async function initApiUrl(): Promise<void> {
  if (Config.RN_ENVIRONMENT !== 'production') { return; }

  try {
    await remoteConfig().setDefaults({api_base_url: ''});
    await remoteConfig().fetchAndActivate();
    const remoteUrl = remoteConfig().getValue('api_base_url').asString().trim();
    if (remoteUrl) {
      setApiUrl(`${remoteUrl}/v1`);
    }
  } catch (error) {
    console.warn('[API] Erro ao buscar URL via Remote Config, usando fallback:', error);
  }
}

export { api, apiv2, apiUrl, apiUrlv2, apiRastrear, apiZignet, apiFipe, apiLecupon, apiMasterClin };
